import Reveal from "@/components/base/Reveal";

export default function Gallery() {
  return (
    <section
      id="gallery"
      className="w-full px-6 md:px-10 py-24 md:py-32 border-t border-background-200"
    >
      {/* Section header row */}
      <Reveal y={30}>
        <div className="flex items-end justify-between mb-12 md:mb-16">
          <p className="font-label text-[11px] tracking-[0.22em] uppercase text-foreground-500">
            <span>04 — Studies</span>
          </p>
          <p className="font-label text-[11px] tracking-[0.22em] uppercase text-foreground-500 hidden md:block">
            <span>Silence, at four angles</span>
          </p>
        </div>
      </Reveal>

      {/* Editorial intro row */}
      <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-24 mb-16 md:mb-20">
        <Reveal delay={120} y={40}>
          <h2 className="font-heading font-semibold text-[48px] md:text-[68px] leading-[0.95] tracking-[-0.03em] text-foreground-950 max-w-lg">
            Looking at it <br />
            <span className="italic font-normal text-foreground-700">
              — a little longer.
            </span>
          </h2>
        </Reveal>
        <Reveal delay={260} y={40}>
          <div className="max-w-md self-end">
            <p className="text-[13px] leading-[1.75] text-foreground-700 text-justify">
              A small series of studies made in the same room, under the same
              slow afternoon light. No renderings, no retouching — only the
              object, the paper, and the shadow it draws.
            </p>
          </div>
        </Reveal>
      </div>

      {/* Editorial gallery grid */}
      <div className="grid grid-cols-12 gap-4 md:gap-6">
        {/* Tall left */}
        <Reveal className="col-span-12 md:col-span-5" y={60} duration={1000}>
          <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-background-100 border border-background-200">
            <img
              src="https://readdy.ai/api/search-image?query=Minimalist%20product%20still%20life%20of%20a%20matte%20black%20rounded%20AI%20voice%20device%20standing%20alone%20on%20a%20soft%20warm%20off%20white%20paper%20surface%2C%20dramatic%20single%20side%20light%20from%20left%2C%20long%20soft%20shadow%2C%20muted%20neutral%20background%2C%20editorial%20fine%20art%20industrial%20design%20photography%2C%20calm%20quiet%20atmosphere%2C%20refined%20negative%20space&width=1200&height=1600&seq=topbud-gal-01&orientation=portrait"
              alt="Study N°01 — Silence"
              className="w-full h-full object-cover object-top transition-transform duration-700 hover:scale-[1.03]"
            />
            <div className="absolute inset-x-0 bottom-0 p-4 md:p-6 flex items-end justify-between">
              <div>
                <p className="font-label text-[10px] tracking-[0.22em] uppercase text-background-50/80">
                  N°01
                </p>
                <p className="mt-1 font-heading text-[22px] leading-none tracking-[-0.02em] text-background-50">
                  Silence
                </p>
              </div>
              <span className="font-label text-[10px] tracking-[0.22em] uppercase text-background-50/70">
                Portrait / 3:4
              </span>
            </div>
          </div>
        </Reveal>

        {/* Right stack */}
        <div className="col-span-12 md:col-span-7 grid grid-cols-1 gap-4 md:gap-6">
          <Reveal delay={150} y={50} duration={1000}>
            <div className="relative aspect-[16/10] rounded-lg overflow-hidden bg-background-100 border border-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=Wide%20editorial%20still%20life%20of%20a%20small%20matte%20black%20AI%20assistant%20device%20resting%20on%20a%20folded%20warm%20gray%20textile%20with%20subtle%20wrinkles%2C%20side%20light%20from%20left%2C%20soft%20shadow%2C%20refined%20negative%20space%2C%20minimalist%20high%20end%20product%20photography%2C%20muted%20monochrome%20warm%20palette&width=1600&height=1000&seq=topbud-gal-02&orientation=landscape"
                alt="Study N°02 — Rest"
                className="w-full h-full object-cover object-top transition-transform duration-700 hover:scale-[1.03]"
              />
              <div className="absolute inset-x-0 bottom-0 p-4 md:p-6 flex items-end justify-between">
                <div>
                  <p className="font-label text-[10px] tracking-[0.22em] uppercase text-background-50/80">
                    N°02
                  </p>
                  <p className="mt-1 font-heading text-[22px] leading-none tracking-[-0.02em] text-background-50">
                    Rest
                  </p>
                </div>
                <span className="font-label text-[10px] tracking-[0.22em] uppercase text-background-50/70">
                  Landscape / 16:10
                </span>
              </div>
            </div>
          </Reveal>

          <div className="grid grid-cols-2 gap-4 md:gap-6">
            <Reveal delay={300} y={50} duration={1000}>
              <div className="relative aspect-square rounded-lg overflow-hidden bg-background-100 border border-background-200">
                <img
                  src="https://readdy.ai/api/search-image?query=Extreme%20close%20up%20macro%20detail%20of%20the%20smooth%20matte%20black%20curved%20surface%20of%20a%20minimalist%20AI%20device%20with%20a%20single%20subtle%20highlight%20arc%2C%20monochrome%20muted%20warm%20gray%20background%2C%20dramatic%20soft%20studio%20lighting%2C%20editorial%20industrial%20design%20texture%20study%2C%20refined%20negative%20space&width=1000&height=1000&seq=topbud-gal-03&orientation=squarish"
                  alt="Study N°03 — Surface"
                  className="w-full h-full object-cover object-top transition-transform duration-700 hover:scale-[1.03]"
                />
                <div className="absolute left-4 bottom-4">
                  <p className="font-label text-[10px] tracking-[0.22em] uppercase text-background-50/80">
                    N°03
                  </p>
                  <p className="mt-1 font-heading text-[20px] leading-none tracking-[-0.02em] text-background-50">
                    Surface
                  </p>
                </div>
              </div>
            </Reveal>

            <Reveal delay={450} y={50} duration={1000}>
              <div className="relative aspect-square rounded-lg overflow-hidden bg-background-100 border border-background-200">
                <img
                  src="https://readdy.ai/api/search-image?query=Extreme%20macro%20detail%20of%20a%20single%20deep%20dark%20circular%20glass%20lens%20eye%20on%20a%20matte%20black%20AI%20device%2C%20soft%20reflection%2C%20monochrome%20warm%20gray%20background%2C%20dramatic%20studio%20lighting%2C%20editorial%20product%20photography%2C%20refined%20detail%20study%2C%20minimalist%20futuristic%20industrial%20aesthetic&width=1000&height=1000&seq=topbud-gal-04&orientation=squarish"
                  alt="Study N°04 — Eye"
                  className="w-full h-full object-cover object-top transition-transform duration-700 hover:scale-[1.03]"
                />
                <div className="absolute left-4 bottom-4">
                  <p className="font-label text-[10px] tracking-[0.22em] uppercase text-background-50/80">
                    N°04
                  </p>
                  <p className="mt-1 font-heading text-[20px] leading-none tracking-[-0.02em] text-background-50">
                    Eye
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>

      {/* Footer meta row */}
      <Reveal delay={200} y={30}>
        <div className="mt-14 md:mt-16 flex items-center justify-between border-t border-background-200 pt-6">
          <div className="flex items-center gap-4">
            <span className="h-px w-10 bg-foreground-400" />
            <span className="font-label text-[11px] tracking-[0.22em] uppercase text-foreground-600">
              Shot on 35mm, Studio 04
            </span>
          </div>
          <span className="font-label text-[11px] tracking-[0.22em] uppercase text-foreground-500">
            04 studies / one object
          </span>
        </div>
      </Reveal>
    </section>
  );
}